import { useMemo } from "react";
import { getCurrentCameraArmedState } from "@src/features/alarms/utils/alarm-utils";
import { isArmedFromArmingType } from "@src/features/schedules/utils/schedule-utils";
import { getCameraKeyString, isLegacyAppCamera } from "@src/utils/key-parser";
import { useAlarm } from "@src/services/api/hooks";
import { usePeriodicRefresh } from "@src/hooks/usePeriodicRefresh";
import type { CameraApiResponse } from "@src/services/api/cameras-api";

interface UseCameraArmedStatusOptions {
  camera: CameraApiResponse;
  siteId: string;
}

export function useCameraArmedStatus({ camera, siteId }: UseCameraArmedStatusOptions) {
  const cameraKey = getCameraKeyString(camera);
  const isLegacyApp = isLegacyAppCamera(camera);
  const currentTime = usePeriodicRefresh();

  // TODO: Remove true once API is consolidated in how we handle and return alarm settings
  const shouldFetchAlarm = !isLegacyApp && (!camera.alarmSettings || true);
  const { data: fetchedAlarmDetail } = useAlarm(siteId, shouldFetchAlarm ? cameraKey : undefined);

  const alarmDetail = useMemo(() => {
    if (!isLegacyApp && fetchedAlarmDetail) {
      return fetchedAlarmDetail;
    }
    return camera.alarmSettings;
  }, [isLegacyApp, fetchedAlarmDetail, camera.alarmSettings]);

  const armedState = useMemo(() => {
    if (!alarmDetail) return null;
    return getCurrentCameraArmedState(alarmDetail, currentTime);
  }, [alarmDetail, currentTime]);

  const isArmed = useMemo(() => {
    if (!armedState) return false;
    return isArmedFromArmingType(armedState.armingType);
  }, [armedState]);

  return {
    alarmDetail,
    armedState,
    isArmed,
  };
}
